import React, { FC, useCallback } from 'react';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { AxiosPromise } from 'axios';

import {
  Card,
  CardSectionInfoPairs,
  FormModalHeader,
  FormModalFooter,
  FormModalContent,
  FormInput,
} from 'Shared/components';

import { Modal as SuccessModal } from 'Shared/components/Modal';

import {
  Asset,
  Button,
  CheckmarkIcon,
  InfoIcon,
  LoadingComponent,
  Modal,
  WarningIcon,
} from '@clearcover/atlas';

import {
  formatDateMiddle,
  formatDateShort,
  formatMoney,
  formatNumberToOrdinal,
} from 'Shared/utils';

import styles from './styles.module.css';
import { useLazyApi } from 'Shared/services/Api';
import classNames from 'classnames';
import { usePolicyContext } from 'Policies/utils';
import { GetPaymentsSummaryByTermNumberResponse } from '@clearcover/core-app-types';

export const ordinalDayOptions: Record<string, string> = new Array(28)
  .fill(0)
  .reduce(
    (acc, _, i) => ({
      ...acc,
      [i + 1]: formatNumberToOrdinal(i + 1) + ' of each month',
    }),
    {}
  );

const YUP_AUTO_PAY_DRAFT_SCHEDULE_SCHEMA = Yup.object().shape({
  day: Yup.number().required('Required'),
});

export type EditAutoPayScheduleProps = {
  policyId: string;
  currentAutoPayDraftDay?: number;
  currentDueDay?: number;
  disabled?: boolean;
  disabledTitle?: string;
  autoPayEligibility: {
    eligible: boolean;
    reasons: Array<string>;
  };
  fetchBillingData: () =>
    | AxiosPromise<GetPaymentsSummaryByTermNumberResponse>
    | Promise<void>;
};

export const EditAutoPaySchedule: FC<EditAutoPayScheduleProps> = ({
  policyId,
  currentAutoPayDraftDay,
  currentDueDay,
  disabled = false,
  disabledTitle,
  autoPayEligibility,
  fetchBillingData,
}) => {
  return (
    <Modal
      variant="full"
      data-testid={'change-auto-pay-modal'}
      toggle={(openModal) => (
        <Button
          data-testid={'change-auto-pay-modal-button'}
          className={styles.autoPayChangeButton}
          disabled={disabled}
          title={disabledTitle}
          onClick={openModal}
        >
          Change Autopay Date
        </Button>
      )}
    >
      {(close) => (
        <EditAutoPayScheduleForm
          policyId={policyId}
          currentAutoPayDraftDay={currentAutoPayDraftDay}
          currentDueDay={currentDueDay}
          autoPayEligibility={autoPayEligibility}
          fetchBillingData={fetchBillingData}
          close={close}
        />
      )}
    </Modal>
  );
};

const EditAutoPayScheduleForm: FC<{
  policyId: string;
  close: () => void;
  currentAutoPayDraftDay?: number;
  currentDueDay?: number;
  autoPayEligibility: {
    eligible: boolean;
    reasons: Array<string>;
  };
  fetchBillingData: () =>
    | AxiosPromise<GetPaymentsSummaryByTermNumberResponse>
    | Promise<void>;
}> = ({
  policyId,
  currentAutoPayDraftDay,
  currentDueDay,
  autoPayEligibility,
  fetchBillingData,
  close,
}) => {
  const [{ data, loading }, getAutoPayDraftSchedule] = useLazyApi<{
    paymentSchedule: Array<{ amount: { value: number }; draftDate: string }>;
  }>({
    url: '/payments/getAutoPayDraftSchedule',
  });
  const [
    {
      data: updateAutoPayDraftScheduleData,
      loading: updateAutoPayScheduleLoading,
    },
    updateAutoPayDraftSchedule,
  ] = useLazyApi<{
    paymentSchedule: Array<{ amount: { value: number }; draftDate: string }>;
  }>({
    url: '/payments/updateAutoPayDraftSchedule',
  });

  const handleFormSubmit = useCallback(
    (
      values: { day: number },
      { setStatus, setSubmitting }: FormikHelpers<{ day: number }>
    ) => {
      updateAutoPayDraftSchedule({
        method: 'POST',
        data: {
          policyId,
          day: values.day,
        },
      })
        .then(() => {
          setSubmitting(false);
          fetchBillingData();
        })
        .catch((err) => {
          setSubmitting(false);
          setStatus(err.response.data.message ?? err.message);
        });
    },
    [updateAutoPayDraftSchedule, fetchBillingData, policyId]
  );

  return (
    <Formik
      initialValues={{ day: 0 }}
      onSubmit={handleFormSubmit}
      validationSchema={YUP_AUTO_PAY_DRAFT_SCHEDULE_SCHEMA}
    >
      {({ values, setFieldValue }) => (
        <>
          <FormModalHeader>Change AutoPay date </FormModalHeader>
          <FormModalContent>
            {autoPayEligibility.eligible ? (
              <section className={styles.autoPayScheduleContainer}>
                <header className={styles.autoPayScheduleHeader}>
                  <h2>Select a new autopay date to view payment schedule.</h2>
                  <p>
                    Updating your autopay date without changing your actual
                    payment due date.
                  </p>
                </header>

                <Card
                  data-testid={'auto-pay-selection-container'}
                  className={styles.autoPayScheduleSelectionContainer}
                >
                  <CardSectionInfoPairs
                    data={[currentDueDay, currentAutoPayDraftDay].map((date) =>
                      date
                        ? `${formatNumberToOrdinal(date)} of each month`
                        : '-'
                    )}
                    headers={['Due Date', 'Autopay Date']}
                    className={styles.autoPayScheduleSelectionInfoPairs}
                  />

                  <FormInput
                    label="New autopay date"
                    options={ordinalDayOptions}
                    disabled={loading || updateAutoPayScheduleLoading}
                    variant={'select'}
                    onChange={(value: string) => {
                      setFieldValue('day', +value);
                      getAutoPayDraftSchedule({
                        data: { day: +value, policyId },
                        method: 'POST',
                      }).then(() => {});
                    }}
                  />
                </Card>

                {(data || loading) && (
                  <Card
                    data-testid={'auto-pay-schedule-preview'}
                    className={classNames(styles.autoPaySchedulePreview, {
                      [styles.loading]: loading,
                    })}
                  >
                    {loading ? (
                      <LoadingComponent className={styles.loadingComponent} />
                    ) : (
                      <>
                        <h2>Payment Schedule</h2>
                        <section className={styles.noticeSection}>
                          <p className={styles.noPenaltyNotice}>
                            Dates have changed, but your total premium has not
                            changed. There is no penalty for missing an autopay
                            date.
                          </p>
                          <p className={styles.advanceNotice}>
                            <Asset
                              description={'Advance Notice Info Icon'}
                              className={styles.advanceNoticeIcon}
                              height={16}
                              width={16}
                              icon={InfoIcon}
                            />
                            To ensure you are always paying for your coverage in
                            advance, we will adjust your payment schedule so
                            that no monthly payments are more than 30 days
                            apart.
                          </p>
                        </section>

                        <section className={styles.autoPayDraftScheduleTable}>
                          <div
                            className={classNames(
                              styles.autoPayDraftScheduleHeaderTableHeader,
                              styles.autoPayDraftScheduleHeaderTableRow
                            )}
                          >
                            <div>Payment Date</div>
                            <div>Amount</div>
                          </div>

                          {data.paymentSchedule.map(
                            ({ draftDate, amount }, index) => (
                              <div
                                key={draftDate}
                                className={
                                  styles.autoPayDraftScheduleHeaderTableRow
                                }
                                data-testid={`payment-schedule-${index}`}
                              >
                                <div
                                  className={styles.date}
                                  data-testid={`payment-schedule-date-${index}`}
                                >
                                  {formatDateShort(draftDate)}
                                </div>
                                <div
                                  className={styles.value}
                                  data-testid={`payment-schedule-amount-${index}`}
                                >
                                  {formatMoney(amount.value)}
                                </div>
                              </div>
                            )
                          )}
                        </section>
                      </>
                    )}
                  </Card>
                )}
              </section>
            ) : (
              <IneligibleReasons reasons={autoPayEligibility.reasons} />
            )}

            <SuccessModal
              showModal={updateAutoPayDraftScheduleData !== undefined}
              onClose={() => {
                close();
              }}
              className={styles.successModal}
              disableCurtainClick
            >
              <div
                data-testid="auto-pay-draft-schedule-update-success-modal"
                className={styles.successModalContainer}
              >
                <div className={styles.checkIcon}>
                  <Asset
                    description="Auto Pay Draft Schedule Update Success Modal"
                    className={styles.checkmarkIcon}
                    icon={CheckmarkIcon}
                    height={30}
                    width={30}
                  />
                </div>
                <div className={styles.successModalHeader}>
                  Payment Date Updated
                </div>
                <p
                  className={styles.modalText}
                  data-testid={`auto-pay-draft-schedule-update-success-modal`}
                >
                  Your payments will now be processed on the{' '}
                  {formatNumberToOrdinal(values.day)} of each month starting on{' '}
                  {formatDateMiddle(
                    updateAutoPayDraftScheduleData?.paymentSchedule[0]
                      .draftDate || ''
                  )}
                </p>
                <Button
                  className={styles.okayButton}
                  data-testid="success-modal-okay"
                  size={'md'}
                  variant="primary"
                  onClick={() => {
                    close();
                  }}
                >
                  Okay
                </Button>
              </div>
            </SuccessModal>
          </FormModalContent>

          <FormModalFooter
            close={() => {
              close();
            }}
            identifier={'edit-auto-pay-schedule'}
          ></FormModalFooter>
        </>
      )}
    </Formik>
  );
};

export const IneligibleReasons: FC<{ reasons: string[] }> = ({ reasons }) => {
  const {
    person: { name },
  } = usePolicyContext();
  return (
    <section className={styles.ineligibleReasonsContainer}>
      <Asset
        description={'Ineligible Alert'}
        icon={WarningIcon}
        className={styles.ineligibleAlertIcon}
        height={14}
        width={14}
      />
      <p className={styles.ineligibleMessage}>
        Unfortunately {name} is not eligible due to:
      </p>
      <ol className={styles.ineligibleReasonsList}>
        {reasons.map((reason, index) => (
          <li data-testid={`auto-pay-reason-${index}`} key={reason}>
            {reason}
          </li>
        ))}
      </ol>
    </section>
  );
};
